import { type FieldArrayPath, type Path, useFieldArray, useFormContext } from 'react-hook-form';
import type {
  DefinitionColumnConfig,
  DefinitionDataSection,
  LocalizedLabel,
  ReportDefinition,
  ReportDefinitionData,
} from '../../types';
import { useDefinitionContext } from '../contexts/DefinitionContext';

export interface UseSectionEditorResult {
  sectionIndex: number;
  columnFields: Array<{ id: string }>;
  handleSectionLabelChange: (label: LocalizedLabel | undefined) => void;
  handleAddColumn: (columnId: string) => void;
  handleRemoveColumn: (columnIndex: number) => void;
  handleReorderColumn: (fromIndex: number, toIndex: number) => void;
  handleUpdateColumnLabel: (columnIndex: number, label: LocalizedLabel | undefined) => void;
  handleUpdateColumnType: (
    columnIndex: number,
    config: NonNullable<DefinitionDataSection['columns']>[number]['config'],
  ) => void;
  appendColumn: (column: NonNullable<DefinitionDataSection['columns']>[number]) => void;
  removeColumn: (index: number) => void;
  moveColumn: (fromIndex: number, toIndex: number) => void;
}

export function useSectionEditor(sectionId: string): UseSectionEditorResult {
  const { blueprint } = useDefinitionContext();
  const { watch, setValue, control } = useFormContext<ReportDefinition>();
  const data = watch('data');
  const sections = (data?.sections || []) as ReportDefinitionData['sections'];
  const sectionIndex = sections.findIndex((s) => s.id === sectionId);

  const blueprintSection = blueprint.sections.find((s) => s.id === sectionId);

  // Use useFieldArray for columns management
  const {
    fields: columnFields,
    append: appendColumn,
    remove: removeColumn,
    move: moveColumn,
  } = useFieldArray({
    control,
    name: `data.sections.${sectionIndex}.columns` as FieldArrayPath<ReportDefinition>,
    shouldUnregister: false,
  });

  const handleSectionLabelChange = (label: LocalizedLabel | undefined) => {
    if (sectionIndex === -1 || !blueprintSection) return;
    setValue(
      `data.sections.${sectionIndex}.label` as Path<ReportDefinition>,
      label ?? blueprintSection.label,
      {
        shouldDirty: true,
      },
    );
  };

  const handleAddColumn = (columnId: string) => {
    if (sectionIndex === -1 || !blueprintSection) return;

    const blueprintColumn = blueprintSection.columns.find((c) => c.id === columnId);
    if (!blueprintColumn) return;

    // Check if column already exists
    const existingIndex = columnFields.findIndex((c) => c.id === columnId);
    if (existingIndex !== -1) return;

    type ColumnType = NonNullable<DefinitionDataSection['columns']>[number];

    const newColumn: ColumnType = {
      id: columnId,
      config: blueprintColumn.config as DefinitionColumnConfig,
      label: blueprintColumn.defaultLabel as LocalizedLabel,
    };

    appendColumn(newColumn);
  };

  const handleRemoveColumn = (columnIndex: number) => {
    if (sectionIndex === -1) return;
    removeColumn(columnIndex);
    // Note: Orphaned sorting rules are automatically cleaned up by useSortingEditor hook
  };

  const handleReorderColumn = (fromIndex: number, toIndex: number) => {
    if (sectionIndex === -1) return;
    moveColumn(fromIndex, toIndex);
  };

  const handleUpdateColumnLabel = (columnIndex: number, label: LocalizedLabel | undefined) => {
    if (sectionIndex === -1 || !blueprintSection) return;
    const blueprintColumn = blueprintSection.columns.find(
      (c) => c.id === columnFields[columnIndex]?.id,
    );
    setValue(
      `data.sections.${sectionIndex}.columns.${columnIndex}.label` as Path<ReportDefinition>,
      label ?? blueprintColumn?.defaultLabel ?? { en: '', es: '' },
      { shouldDirty: true },
    );
  };

  const handleUpdateColumnType = (
    columnIndex: number,
    config: NonNullable<DefinitionDataSection['columns']>[number]['config'],
  ) => {
    if (sectionIndex === -1) return;
    setValue(
      `data.sections.${sectionIndex}.columns.${columnIndex}.config` as Path<ReportDefinition>,
      config,
      {
        shouldDirty: true,
      },
    );
  };

  return {
    sectionIndex,
    columnFields,
    handleSectionLabelChange,
    handleAddColumn,
    handleRemoveColumn,
    handleReorderColumn,
    handleUpdateColumnLabel,
    handleUpdateColumnType,
    appendColumn,
    removeColumn,
    moveColumn,
  };
}
